/**
 * == Controller: PlatformBaseController
 *
 * Shared Stimulus superclass for platform dashboards. Centralises navigation
 * highlighting, loader visibility, fetch helpers, and persistent filter state
 * management so feature controllers can stay focused on domain logic.
 *
 * === Highlights:
 * - Keeps menus in sync with the current URL via `highlight`.
 * - Wraps `fetch` with Turbo Stream rendering, loader toggling, and callbacks.
 * - Provides session helpers for storing filter selections across page loads.
 */
import { Controller } from "@hotwired/stimulus"
import {Turbo} from "@hotwired/turbo-rails"

export default class extends Controller {

    static targets = [ "menuLink", "submenu", "subsubmenu" ];

    static classes = ["active"];

    static values = {
        menuKeyValue: {type: String, default: undefined},
        subMenuKeyValue: {type: String, default: undefined},
        trackVisitDatetime: {type: Boolean, default: false}
    }

    connect() {
        this.highlight()
        document.addEventListener("turbo:load", this.highlight)
        window.addEventListener("popstate", this.highlight)
        if (this.trackVisitDatetimeValue) {
            this.trackVisitHandler = this.trackVisit.bind(this)
            window.addEventListener("beforeunload", this.trackVisitHandler)
            window.addEventListener("pagehide", this.trackVisitHandler)
            document.addEventListener("turbo:before-visit", this.trackVisitHandler)
        }
    }

    disconnect() {
        document.removeEventListener("turbo:load", this.highlight)
        window.removeEventListener("popstate", this.highlight)
        if (this.trackVisitHandler) {
            window.removeEventListener("beforeunload", this.trackVisitHandler)
            window.removeEventListener("pagehide", this.trackVisitHandler)
            document.removeEventListener("turbo:before-visit", this.trackVisitHandler)
            this.trackVisitHandler = null
        }
    }

    highlight = () => {
        const keys = this.prefixKeysFrom(location.pathname);

        const menuKeys = this.menuLinkTargets.map(link => link.dataset.menuKeyValue);
        const activeMenuKey = [...keys].reverse().find(key => menuKeys.includes(key));
        this.menuLinkTargets.forEach(link =>
            link.classList.toggle("active", link.dataset.menuKeyValue === activeMenuKey)
        );

        if (this.submenuTargets) {
            const submenuKeys = this.submenuTargets.map(submenu => submenu.dataset.menuKeyValue);
            const activeSubmenuKey = [...keys].reverse().find(key => submenuKeys.includes(key));
            this.submenuTargets.forEach(submenu =>
                submenu.classList.toggle("active", submenu.dataset.menuKeyValue === activeSubmenuKey)
            );
            this.menuLinkTargets.forEach(link =>
                link.classList.remove("active")
            );
        }

        if (this.subsubmenuTargets) {
            const subsubmenuKeys = this.subsubmenuTargets.map(subsubmenu => subsubmenu.dataset.menuKeyValue);
            const activeSubsubmenuKey = [...keys].reverse().find(key => subsubmenuKeys.includes(key));
            this.subsubmenuTargets.forEach(subsubmenu =>
                subsubmenu.classList.toggle("active", subsubmenu.dataset.menuKeyValue === activeSubsubmenuKey)
            );
        }
    }

    prefixKeysFrom(path) {
        const parts = path.replace(/^\/|\/$/g, "").split("/")
        return parts.map((_p, idx) => parts.slice(0, idx + 1).join("-"))
    }

    toggleMenu(event) {
        event.preventDefault()
        const key = event.currentTarget.dataset.menuKeyValue
        const submenu = this.submenuTargets.find(s => s.dataset.menuKeyValue === key)
        if (submenu) submenu.classList.toggle("active")
    }

    download(event) {
        event.preventDefault();
        this.showLoader();
        this.downloadTarget.disabled = true;

        const poll = () => {
            fetch(this.readyUrlValue, { headers: {Accept: "application/json"} })
                .then(r => r.json())
                .then(data => {
                    if (data.ready) {
                        this.hideLoader();
                        this.downloadTarget.disabled = false;
                        // Start download
                        window.location = this.downloadUrlValue;
                    } else {
                        setTimeout(poll, 1000);
                    }
                })
                .catch(() => {
                    // Optionally show a different error spinner
                    setTimeout(poll, 2000);
                });
        }

        poll();
    }

    // AJAX fetch and Turbo stream update
    fetchAction(url, {
        method = "get",
        headers = {},
        body = null,
        onSuccess = null,
        onError = null,
        accept = "text/vnd.turbo-stream.html"
    } = {}) {
        this.showLoader();

        const fetchHeaders = {
            Accept: accept,
            ...headers
        };

        const options = {
            method: method.toUpperCase(),
            headers: fetchHeaders,
            credentials: "same-origin",
            ...(body && { body })
        };

        return fetch(url, options)
            .then(response => {
                if (!response.ok) throw new Error('Network response was not ok')
                return accept.includes("json") ? response.json() : response.text()
            })
            .then(data => {
                // Turbo Stream: default render, otherwise optionally handle differently
                if (accept === "text/vnd.turbo-stream.html") {
                    Turbo.renderStreamMessage(data)
                }
                if (typeof onSuccess === "function") onSuccess(data)
                return data
            })
            .catch(err => {
                if (typeof onError === "function") return onError(err)
                alert("Error loading data")
                throw err
            })
            .finally(() => this.hideLoader());
    }

    getSearch(form) {
        return form.querySelector('[name="search"]')?.value;
    }

    // Utility to get project_id from form
    getProjectId(form) {
        return form.querySelector('[name="project_id"]')?.value;
    }

    // Loader UI handling
    showLoader() {
        document.querySelector('.loader-overlay')?.classList.remove('hidden');
        const loader = document.querySelector("#loader");
        if (loader) loader.style.display = "";
    }

    hideLoader() {
        document.querySelector('.loader-overlay')?.classList.add('hidden');
        const loader = document.querySelector("#loader");
        if (loader) loader.style.display = "none";
    }

    // Delete a key from session-backed filters
    deletePersistentFilter(key) {
        this.updateSession({ [key]: null });
    }

    // Persist a key/value to session-backed filters
    setPersistentFilter(key, value) {
        this.updateSession({ [key]: value });
    }

    getPersistedFilter(form, key) {
        return form?.querySelector?.(`[name="${key}"]`)?.value;
    }

    searchToggle(event) {
        const docsHeader = document.querySelector('.docs_header');
        const toggleBtn = event.currentTarget;
        if (!docsHeader || !toggleBtn) return;

        const docsHeaderRect = docsHeader.getBoundingClientRect();
        const toggleRect = toggleBtn.getBoundingClientRect();

        // Compute width
        const w = toggleRect.x - docsHeaderRect.x + toggleRect.width + 20;

        // Set width and show with animation
        const searchEl = document.querySelector('.docs_header_search');
        if (searchEl) {
            searchEl.style.width = w + 'px';
            searchEl.style.display = 'block';   // always show
            searchEl.style.opacity = 0;
            searchEl.style.transition = "opacity 0.3s";
            requestAnimationFrame(() => {
                searchEl.style.opacity = 1;

                const inputEl = document.querySelector('#search');
                if (inputEl) {
                    inputEl.focus();
                }
            });
        }
    }

    removeParam(paramName) {
        const url = new URL(window.location.href);
        const params = url.searchParams;

        if (params.has(paramName)) {
            params.delete(paramName);
            url.search = params.toString();
            history.replaceState({}, '', url.toString());
        }
    }

    closeSearch(event) {
        const element = document.querySelector('.docs_header_search');
        if (element) {
            element.style.transition = 'opacity .3s';
            element.style.opacity = 1;
            requestAnimationFrame(() => {
                element.style.opacity = 0;
                setTimeout(() => {
                    element.style.display = 'none';
                    element.style.opacity = 1;
                }, 300);
            });
        }

        window.location.reload()
    }

    filtersToggle(event) {
        event.preventDefault();
        document.querySelector('.docs_header_filter_toggle')?.classList.toggle('active');
        const block = document.querySelector(window.innerWidth < 992 ? '#filter_block_mob' : '#filter_block');
        if (block) block.style.display = block.style.display === 'none' ? '' : 'none';
    }

    filtersHide(event) {
        event.preventDefault();
        document.querySelector('.docs_header_filter_toggle')?.classList.remove('active');
        const block = document.querySelector(window.innerWidth < 992 ? '#filter_block_mob' : '#filter_block');
        if (block) block.style.display = 'none';
    }

    prevent(event) {
        event.preventDefault();
    }

    startOfLocalDay(d) {
        const x = new Date(d.getTime())
        x.setHours(0, 0, 0, 0)
        return x
    }

    addDays(date, days) {
        const d = new Date(date.getTime())
        d.setDate(d.getDate() + days)
        d.setHours(0, 0, 0, 0)
        return d
    }

    formatYMD(date) {
        const y = date.getFullYear()
        const m = String(date.getMonth() + 1).padStart(2, "0")
        const d = String(date.getDate()).padStart(2, "0")
        return `${y}-${m}-${d}`
    }

    toLocalDateOnly(input) {
        if (!input) return this.startOfLocalDay(new Date())
        const m = String(input).match(/^(\d{4})-(\d{2})-(\d{2})$/)
        if (m) return this.startOfLocalDay(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
        const d = new Date(input)
        return this.startOfLocalDay(d)
    }

    updateSession(data = {}) {
        const payload = JSON.stringify({ updates: data })
        const token = document.querySelector('meta[name="csrf-token"]')?.content
        return fetch("/platform/session/update", {
            method: "post",
            headers: {
                "Content-Type": "application/json",
                ...(token ? { "X-CSRF-Token": token } : {})
            },
            body: payload,
            credentials: "same-origin"
        }).catch(() => {})
    }

    trackVisit() {
        if (this.visitTracked) return
        this.visitTracked = true
        this.recordControllerVisit()
    }

    recordControllerVisit() {
        const token = document.querySelector('meta[name="csrf-token"]')?.content
        const params = new URLSearchParams()
        if (token) params.set("authenticity_token", token)
        params.set(`updates[controller_visits][${this.identifier}]`, new Date().toISOString())
        const payload = params.toString()
        if (navigator.sendBeacon) {
            try {
                const blob = new Blob([payload], {
                    type: "application/x-www-form-urlencoded; charset=UTF-8"
                })
                navigator.sendBeacon("/platform/session/update", blob)
                return
            } catch (_e) {
                // fall back to fetch
            }
        }

        return fetch("/platform/session/update", {
            method: "post",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                ...(token ? { "X-CSRF-Token": token } : {})
            },
            body: payload,
            credentials: "same-origin",
            keepalive: true
        }).catch(() => {})
    }
}
